import metrics from "./fontMetrics.json";
import { snap } from "./units";

// Port of the Nohaya Career app's render_fonts.dart: the 22 typefaces the
// templates and the resume builder's font picker offer. Every font file is
// registered as its own family name (in CSS and in react-pdf alike) so a
// style names one exact file and nothing depends on weight/style matching or
// synthetic bold/italic.

export type FontStem = keyof typeof metrics;

export type FontFace = {
  regular: FontStem;
  italic: FontStem;
  semiBold: FontStem;
  bold: FontStem;
  extraBold: FontStem;
};

export const Fam = {
  inter: "inter",
  lato: "lato",
  montserrat: "montserrat",
  merriweather: "merriweather",
  lora: "lora",
  crimson: "crimson",
  playfair: "playfair",
  mono: "jetbrains",
  roboto: "roboto",
  openSans: "openSans",
  sourceSans: "sourceSans",
  ibmPlexSans: "ibmPlexSans",
  nunitoSans: "nunitoSans",
  workSans: "workSans",
  dmSans: "dmSans",
  poppins: "poppins",
  raleway: "raleway",
  sourceSerif: "sourceSerif",
  ptSerif: "ptSerif",
  libreBaskerville: "libreBaskerville",
  ebGaramond: "ebGaramond",
  ibmPlexMono: "ibmPlexMono",
} as const;

export type FamilyId = (typeof Fam)[keyof typeof Fam];

/** Standard four-file family (`<stem>Regular/Italic/SemiBold/Bold`). */
function four(stem: string, semiBold = true): FontFace {
  const s = (suffix: string) => `${stem}${suffix}` as FontStem;
  return {
    regular: s("Regular"),
    italic: s("Italic"),
    semiBold: semiBold ? s("SemiBold") : s("Bold"),
    bold: s("Bold"),
    extraBold: s("Bold"),
  };
}

// Families without a true semi-bold/extra-bold reuse the nearest weight,
// exactly as in the app.
export const FAMILIES: Record<FamilyId, FontFace> = {
  inter: { regular: "interRegular", italic: "interItalic", semiBold: "interSemiBold", bold: "interBold", extraBold: "interBold" },
  lato: { regular: "latoRegular", italic: "latoItalic", semiBold: "latoBold", bold: "latoBold", extraBold: "latoBold" },
  montserrat: {
    regular: "montserratRegular",
    italic: "montserratItalic",
    semiBold: "montserratSemiBold",
    bold: "montserratBold",
    extraBold: "montserratExtraBold",
  },
  merriweather: {
    regular: "merriweatherRegular",
    italic: "merriweatherItalic",
    semiBold: "merriweatherBold",
    bold: "merriweatherBold",
    extraBold: "merriweatherBold",
  },
  lora: four("lora"),
  crimson: {
    regular: "crimsonProRegular",
    italic: "crimsonProItalic",
    semiBold: "crimsonProSemiBold",
    bold: "crimsonProBold",
    extraBold: "crimsonProBold",
  },
  playfair: {
    regular: "playfairDisplayRegular",
    italic: "playfairDisplayItalic",
    semiBold: "playfairDisplayBold",
    bold: "playfairDisplayBold",
    extraBold: "playfairDisplayBold",
  },
  jetbrains: {
    regular: "jetBrainsMonoRegular",
    italic: "jetBrainsMonoRegular",
    semiBold: "jetBrainsMonoBold",
    bold: "jetBrainsMonoBold",
    extraBold: "jetBrainsMonoBold",
  },
  roboto: four("roboto", false),
  openSans: four("openSans"),
  sourceSans: four("sourceSans"),
  ibmPlexSans: four("ibmPlexSans"),
  nunitoSans: four("nunitoSans"),
  workSans: four("workSans"),
  dmSans: four("dmSans"),
  poppins: four("poppins"),
  raleway: four("raleway"),
  sourceSerif: four("sourceSerif"),
  ptSerif: four("ptSerif", false),
  libreBaskerville: four("libreBaskerville", false),
  ebGaramond: four("ebGaramond"),
  ibmPlexMono: four("ibmPlexMono"),
};

export function isFamilyId(id: string | null | undefined): id is FamilyId {
  return !!id && Object.prototype.hasOwnProperty.call(FAMILIES, id);
}

/**
 * Optical size correction so every body family reads at the same size as
 * Inter (templates are tuned in Inter-equivalent points) — the app's values.
 */
export function familyScale(family: FamilyId): number {
  switch (family) {
    case "crimson":
      return 1.12;
    case "merriweather":
      return 0.93;
    case "montserrat":
      return 0.95;
    case "lato":
      return 1.04;
    case "lora":
      return 1.02;
    case "roboto":
      return 1.04;
    case "openSans":
      return 1.0;
    case "sourceSans":
      return 1.08;
    case "ibmPlexSans":
      return 1.03;
    case "nunitoSans":
      return 1.03;
    case "workSans":
      return 0.97;
    case "dmSans":
      return 1.02;
    case "poppins":
      return 0.95;
    case "raleway":
      return 1.01;
    case "sourceSerif":
      return 1.0;
    case "ptSerif":
      return 1.04;
    case "libreBaskerville":
      return 0.94;
    case "ebGaramond":
      return 1.15;
    case "playfair":
      return 1.03;
    case "ibmPlexMono":
    case "jetbrains":
      return 0.9;
    default:
      return 1.0;
  }
}

/** The family name a stem is registered under (CSS @font-face and react-pdf). */
export function fontFamilyName(stem: FontStem): string {
  return `nr-${stem}`;
}

/**
 * Height of one line box in the app's `pdf` renderer: hhea ascent minus
 * descent. Extra `lineSpacing` is added between lines, never after the last.
 */
export function lineBox(stem: FontStem, size: number): number {
  const m = metrics[stem];
  return (m.ascent - m.descent) * size;
}

export function fontAscent(stem: FontStem): number {
  return metrics[stem].ascent;
}

/** hhea descent as a positive fraction of the em. */
export function fontDescent(stem: FontStem): number {
  return -metrics[stem].descent;
}

/**
 * Vertical metrics of a text block that both renderers share. One line pitch
 * for every line — the tallest run's line box plus the block's lineSpacing —
 * and the baseline at the tallest run ascent below the line top (react-pdf's
 * rule). Browsers instead grow a line to the union of every run's own inline
 * box, built from pixel-rounded platform metrics, so a line mixing fonts or
 * sizes came out up to a pixel taller on screen than in the PDF; the HTML
 * renderer pins its lines to these numbers instead (see renderHtml.tsx).
 */
export function textBlockMetrics(runs: { font: FontStem; size: number }[], lineSpacing: number): {
  /** Line pitch (pt). */
  lineHeight: number;
  /** Tallest run ascent (pt): the baseline's distance below each line top. */
  ascent: number;
} {
  let lineHeight = 0;
  let ascent = 0;
  for (const r of runs) {
    const size = snap(r.size);
    lineHeight = Math.max(lineHeight, lineBox(r.font, size) + snap(lineSpacing));
    ascent = Math.max(ascent, fontAscent(r.font) * size);
  }
  return { lineHeight: snap(lineHeight), ascent };
}

/**
 * Top and bottom margins (pt, on the layout grid) of a text block in the
 * HTML renderer: they put the first baseline at the tallest run ascent below
 * the block's top and make the block take exactly lines × pitch −
 * lineSpacing. For a single run this is about −lineSpacing/2 on each side.
 */
export function textBlockMargins(runs: { font: FontStem; size: number }[], lineSpacing: number): [number, number] {
  const first = runs[0];
  if (!first) return [0, 0];
  const { lineHeight, ascent } = textBlockMetrics(runs, lineSpacing);
  // Baseline of the strut (the block's own font) within a line box, per the
  // CSS half-leading model.
  const strutBaseline = (lineHeight + (fontAscent(first.font) - fontDescent(first.font)) * snap(first.size)) / 2;
  const top = snap(ascent - strutBaseline);
  return [top, -snap(lineSpacing) - top];
}

export const ALL_STEMS = Object.keys(metrics) as FontStem[];

/** Every file of a family (deduplicated). */
export function familyStems(family: FamilyId): FontStem[] {
  const f = FAMILIES[family];
  return [...new Set([f.regular, f.italic, f.semiBold, f.bold, f.extraBold])];
}

/**
 * Code points every bundled subset covers (see scripts/subset-resume-fonts.py).
 * Text outside them falls back to a system font on screen and is missing from
 * the PDF, so the builder warns about it.
 */
const COVERED: [number, number][] = [
  [0x0a, 0x0a],
  [0x20, 0x7e],
  [0xa0, 0x17f],
  [0x2010, 0x2027],
  [0x2030, 0x203a],
  [0x20ac, 0x20ac],
  [0x20b9, 0x20b9],
  [0x2122, 0x2122],
  [0x2192, 0x2192],
  [0x2212, 0x2212],
  [0x25aa, 0x25aa],
  [0x25cf, 0x25cf],
];

export function isCoveredCodePoint(cp: number): boolean {
  for (const [a, b] of COVERED) if (cp >= a && cp <= b) return true;
  return false;
}

/** Characters in [text] the resume fonts cannot draw (deduplicated). */
export function uncoveredChars(text: string): string[] {
  const out = new Set<string>();
  for (const ch of text) {
    const cp = ch.codePointAt(0)!;
    if (!isCoveredCodePoint(cp)) out.add(ch);
  }
  return [...out];
}
